package org.example.project.engineSnap

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.example.project.dashboard.ContentOffset
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.service.CustomerApi
import org.example.project.service.LoginService
import org.example.project.service.RollenService
import org.example.project.service.SnapApi
import org.example.project.service.SnapPopupService
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun SnapPopupOverlayLayer(node: String) {
    val popups = SnapPopupService.getPopups(node)

    popups.forEachIndexed { index, popup ->
        key(popup.id) {
            var showSnapHints by remember(popup.id) { mutableStateOf(false) }
            PopupEngineCard(
            windowKey = popup.id,
            widthPx = 760,
            heightPx = 500,
            minWidthPx = 700,
            minHeightPx = 460,
            initialX = 360.0 + ((index % 4) * 24.0),
            initialY = 160.0 + ((index % 4) * 20.0),
            contentInsetLeft = ContentOffset.LEFT.value.toDouble(),
            contentInsetTop = ContentOffset.TOP.value.toDouble(),
            contentInsetRight = ContentOffset.RIGHT.value.toDouble(),
            contentInsetBottom = ContentOffset.BOTTOM.value.toDouble(),
            onClose = {
                if (showSnapHints) showSnapHints = false
                else SnapPopupService.closePopup(node, popup.id)
            }
        ) {
                SnapPopupCard(
                    popup = popup,
                    showSnapHints = showSnapHints,
                    onShowSnapHints = { showSnapHints = true },
                    onHideSnapHints = { showSnapHints = false },
                    onClose = { SnapPopupService.closePopup(node, popup.id) }
                )
        }
    }
}
}

@Composable
private fun SnapPopupCard(
    popup: All,
    showSnapHints: Boolean,
    onShowSnapHints: () -> Unit,
    onHideSnapHints: () -> Unit,
    onClose: () -> Unit
) {
    var hintsOpened by remember(popup.id) { mutableStateOf(false) }
    var hintsRead by remember(popup.id) { mutableStateOf(false) }
    var loading by remember(popup.id) { mutableStateOf(false) }
    var info by remember(popup.id) { mutableStateOf("") }
    var senderCompany by remember(popup.id) { mutableStateOf<All?>(null) }

    val user = LoginService.getUser()
    val currentUserName = user?.vorname?.trim().orEmpty()
    val ownCompanyName = user?.firma?.ifBlank { user.kunde }.orEmpty()
    val senderFirma = popup.firma.ifBlank { popup.kunde.ifBlank { popup.firmenId } }
    val canRespond = RollenService.hasActionPermission("Snapverwaltung", RollenService.ACTION_EDIT) ||
        RollenService.hasActionPermission("Snapverwaltung", RollenService.ACTION_SAVE)

    val senderPlz = senderCompany?.plz?.takeIf { it.isNotBlank() } ?: popup.plz
    val senderStrasse = senderCompany?.strasse?.takeIf { it.isNotBlank() } ?: popup.strasse
    val senderHausnummer = senderCompany?.hausnummer?.takeIf { it.isNotBlank() } ?: popup.hausnummer
    val fullAddress = listOf(senderPlz, senderStrasse, senderHausnummer)
        .filter { it.isNotBlank() }
        .joinToString(" ")

    LaunchedEffect(popup.firmenId) {
        senderCompany = if (popup.firmenId.isBlank()) null else runCatching {
            CustomerApi.loadDetails(popup.firmenId)
        }.getOrNull()
    }

    fun respond(accepted: Boolean) {
        if (loading) return
        if (!canRespond) {
            info = "Keine Schreibrechte für die Snapverwaltung."
            return
        }
        loading = true
        info = ""
        MainScope().launch {
            val response = SnapApi.respond(
                requestId = popup.id,
                userId = LoginService.getId().trim(),
                firmenId = user?.firmenId.orEmpty().trim(),
                accepted = accepted
            )
            loading = false
            if (response?.ok == true) onClose()
            else info = if (accepted) {
                "Snap konnte nicht angenommen werden."
            } else {
                "Snap konnte nicht abgelehnt werden."
            }
        }
    }

    if (showSnapHints) {
        SnapHintsContent(
            senderFirma = senderFirma,
            onClose = onHideSnapHints
        )
        return
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            padding(22.px)
            minHeight(100.percent)
            color(DashboardTheme.TEXT_LIGHT)
        }
    }) {
        Span({
            style {
                fontSize(22.px)
                fontWeight("700")
                textAlign("center")
                width(100.percent)
            }
        }) { Text("Snap Match") }

        Spacer(28)
        Spacer(28)

        Span({
            style {
                fontSize(15.px)
                lineHeight("1.5")
                color(Color("rgba(247,247,245,0.82)"))
                textAlign("center")
                width(100.percent)
            }
        }) {
            Text("Hallo ${currentUserName.ifBlank { "User" }},")
        }

        Spacer(16)

        Span({
            style {
                fontSize(15.px)
                lineHeight("1.5")
                color(Color("rgba(247,247,245,0.82)"))
                textAlign("center")
                width(100.percent)
            }
        }) {
            Text(
                "die Firma $senderFirma ($fullAddress) möchte sich mit deinem Unternehmen ${ownCompanyName.ifBlank { "-" }} verbinden."
            )
        }

        Span({
            style {
                fontSize(14.px)
                color(Color("rgba(247,247,245,0.78)"))
                textAlign("center")
                width(100.percent)
            }
        }) {
            Text("Erfahre hier, welche Vorteile du mit dem Snap erreichst")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(10.px)
                marginTop(10.px)
            }
        }) {
            LumaticaButton(
                label = "SNAP Hinweise",
                onClick = {
                    hintsOpened = true
                    onShowSnapHints()
                },
                variant = if (hintsOpened) LumaticaButtonVariant.Action else LumaticaButtonVariant.Standard,
                enabled = !loading
            )
            LumaticaCheckbox(
                checked = hintsRead,
                onChange = { checked -> if (hintsOpened) hintsRead = checked },
                enabled = hintsOpened
            )
            Span({
                style {
                    fontSize(14.px)
                    color(DashboardTheme.TEXT_LIGHT)
                }
            }) {
                Text("Gelesen")
            }
        }

        if (info.isNotBlank()) {
            Div({
                style {
                    padding(10.px)
                    marginTop(12.px)
                    borderRadius(10.px)
                    backgroundColor(Color("rgba(173,55,55,0.28)"))
                    color(Color("#ffd7d7"))
                    fontSize(13.px)
                }
            }) { Text(info) }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(14.px)
                marginTop(20.px)
            }
        }) {
            LumaticaButton(
                label = if (loading) "..." else "Jetzt verbinden",
                onClick = { respond(true) },
                variant = LumaticaButtonVariant.Action,
                enabled = !loading && hintsRead && canRespond
            )
            LumaticaButton(
                label = if (loading) "..." else "Ich kenne diese Firma nicht",
                onClick = { respond(false) },
                variant = LumaticaButtonVariant.Standard,
                enabled = !loading && canRespond
            )
        }
    }

}

@Composable
private fun Spacer(px: Int) {
    Div({ style { height(px.px) } })
}
